import React, { useCallback } from "react";

type CoordinateInputProps = {
    latitude: number;
    longitude: number;
    onLatitudeChange: (value: number) => void;
    onLongitudeChange: (value: number) => void;
    required?: boolean;
};

export const CoordinateInput: React.FC<CoordinateInputProps> = ({
    latitude,
    longitude,
    onLatitudeChange,
    onLongitudeChange,
    required = false,
}) => {
    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>, field: "lat" | "lng") => {
        const pastedText = e.clipboardData.getData("text");

        // Check if it contains a comma (lat,lng format)
        if (pastedText.includes(",")) {
            e.preventDefault(); // Prevent default paste behavior

            const parts = pastedText.split(",").map((part) => part.trim());
            if (parts.length === 2) {
                const lat = parseFloat(parts[0]);
                const lng = parseFloat(parts[1]);

                if (!isNaN(lat) && !isNaN(lng)) {
                    onLatitudeChange(lat);
                    onLongitudeChange(lng);
                }
            }
        }
    }, [onLatitudeChange, onLongitudeChange]);

    return (
        <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex flex-col relative">
                <label className="mb-1">
                    Latitude
                    {required && <span className="text-dark-red"> *</span>}
                </label>
                <input
                    type="number"
                    min={-90}
                    max={90}
                    className="h-10 w-full shadow-md rounded-md max-h-20 px-4 outline-none bg-white text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={latitude}
                    onChange={(e) => onLatitudeChange(Number(e.target.value))}
                    onPaste={(e) => handlePaste(e, "lat")}
                    placeholder="0.0"
                />
            </div>

            <div className="flex flex-col relative">
                <label className="mb-1">
                    Longitude
                    {required && <span className="text-dark-red"> *</span>}
                </label>
                <input
                    type="number"
                    min={-180}
                    max={180}
                    className="h-10 w-full shadow-md rounded-md max-h-20 px-4 outline-none bg-white text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={longitude}
                    onChange={(e) => onLongitudeChange(Number(e.target.value))}
                    onPaste={(e) => handlePaste(e, "lng")}
                    placeholder="0.0"
                />
            </div>
        </div>
    );
};
